import { Trans, msg } from '@lingui/macro';
import { Redirect, Route, Switch, useParams } from 'react-router-dom';
import { Box, Flex, Text } from '~/design-system';
import {
  ProgressIndicator as BaseProgressIndicator,
  ProgressStep,
} from '../../../../components/Layout/ProgressIndicator';
import { getStepLinks } from '../../../_config';
import NotFound from '../../404';
import { useCommonLinks } from '../../_config';
import Complete from './complete';
import MapFields from './mapFields';
import MapHeader from './mapHeader';
import Name from './name';
import Ready from './ready';
import Validation from './validation';

export function $ImportIdRoutes({ match }) {
  return (
    <Switch>
      <Route path={`${match.path}/name`} component={Name} />
      <Route path={`${match.path}/mapHeader`} component={MapHeader} />
      <Route path={`${match.path}/mapFields`} component={MapFields} />
      <Route path={`${match.path}/validation`} component={Validation} />
      <Route path={`${match.path}/ready`} component={Ready} />
      <Route path={`${match.path}/complete`} component={Complete} />
      {/* The starting route: */}
      <Redirect exact from={`${match.path}`} to={`${match.url}/mapHeader`} />
      <Route component={NotFound} />
    </Switch>
  );
}

export const importType = 'productInternalIdImport';

/** @typedef {keyof ReturnType<getInternalIdsLinks>} Step The possible steps for productInternalIdImport. */

/**
 * @param {object} params
 * @param {string} params.workspaceId
 * @param {string} params.importId
 * @returns
 */
export function getInternalIdsLinks({ workspaceId, importId }) {
  const common = useCommonLinks();
  const baseLink = `/${workspaceId}`;
  const baseImportId = importId;
  return {
    root: common.root,
    uploadFile: () => `${baseLink}/${importType}/uploadFile`,
    /**
     * We might not have set the importId yet if we've just uploaded a new file. We may pass an importId in that case.
     * @param {{ importId?: string}} [options]
     */
    name: (options) =>
      `${baseLink}/${importType}/${options?.importId || baseImportId}/name`,
    mapHeader: () => `${baseLink}/${importType}/${importId}/mapHeader`,
    mapFields: () => `${baseLink}/${importType}/${importId}/mapFields`,
    validation: () => `${baseLink}/${importType}/${importId}/validation`,
    ready: () => `${baseLink}/${importType}/${importId}/ready`,
    complete: () => `${baseLink}/${importType}/${importId}/complete`,
  };
}

export function ProgressIndicator({ step }) {
  const steps = ['uploadFile', 'mapData', 'ready'];
  const currentIndex = steps.indexOf(step);
  const currentStep = currentIndex + 1;

  return (
    <Flex sx={{ justifyContent: 'space-between', paddingX: 40 }}>
      <Box sx={{ width: 150, flexShrink: 0, textAlign: 'left' }}>
        <Text>
          <Trans>Internal IDs</Trans>
        </Text>
      </Box>
      <BaseProgressIndicator currentIndex={currentIndex}>
        <ProgressStep>
          <Trans>Upload file</Trans>
        </ProgressStep>
        <ProgressStep>
          <Trans>Map data</Trans>
        </ProgressStep>
        <ProgressStep>
          <Trans>Confirm</Trans>
        </ProgressStep>
      </BaseProgressIndicator>
      <Box sx={{ width: 150, flexShrink: 0, textAlign: 'right' }}>
        <Text>
          Step {currentStep} of {steps.length}
        </Text>
      </Box>
    </Flex>
  );
}

/**
 * @param {Step} currentStep
 * @returns
 */
export function useLink(currentStep) {
  /** @type {{importId: string}} */
  const { workspaceId, importId } = useParams();
  const urls = getInternalIdsLinks({ workspaceId, importId });
  return getStepLinks(urls, Object.keys(urls), currentStep);
}

export const fieldsToImport = [
  { key: 'product.instanceId', label: msg`Product ID`, required: true },
  { key: 'product.inventoryId', label: msg`Internal ID`, required: true },
  { key: 'product.inventoryId2', label: msg`Internal ID 2` },
];
